<template>
    <v-container fluid>
        <v-card outlined class="card-no-outline">
            <v-card-title class="text-h5 pa-0">{{ $t('quest.main.title') + title }}</v-card-title>
            <v-card-title>
                <v-row>
                    <span class="text-h6 grey--text text--darken-2">
                        {{ $t('quest.main.background') }}
                    </span>
                    <v-spacer></v-spacer>
                    <v-btn small color="grey darken-2" class="normal-case"
                        dark @click="openDiarySetting">
                        <v-icon small>mdi-cog</v-icon>
                        <span class="d-none d-sm-block">{{ $t('info.button.diary_setting') }}</span>
                    </v-btn>
                </v-row>
            </v-card-title>
            <v-card outlined>
                <v-card-title>
                    <v-row>
                        <v-col cols="1" class="my-auto">
                            <v-icon x-large>mdi-book-open-variant</v-icon>
                        </v-col>
                        <v-col cols="11">
                            <v-row>
                                <v-card-title class="text-h6 font-weight-bold 
                                deep-orange--text text--darken-3 mb-0 pb-0">
                                    {{ title }}
                                    <v-btn class="mx-10 normal-case"
                                        outlined color="#25A2B7" small
                                        @click="checkInviteCode">
                                        {{ $t('quest.main.button_invitation_code') }}
                                    </v-btn>
                                    <!-- 深拷貝的分享代碼，只有後端認定能分享時才出現（見 TemplateShareDialog） -->
                                    <v-btn v-if="canShare" class="normal-case"
                                        outlined color="#25A2B7" small
                                        @click="shareDialog = true">
                                        <v-icon small left>mdi-share-variant</v-icon>
                                        {{ $t('unit.share.title') }}
                                    </v-btn>
                                </v-card-title>

                            </v-row>
                            <v-row>
                                <v-card-subtitle class="mt-0 pt-0">
                                    {{ description }}
                                </v-card-subtitle>
                            </v-row>
                        </v-col>
                    </v-row>
                </v-card-title>
            </v-card>
            <!--
                關卡式範本（diary_homework.template 有值）改為導向關卡編輯器。
                見 docs/plans/UnitTemplateEditor.web.spec.md §15。
                ⚠ 只換掉「題目」這一塊——上方的範本名稱、說明、範本代碼、設定按鈕
                全部維持原狀，舊模式的路徑一行未改。
            -->
            <v-card v-if="isUnitTemplate" outlined class="mt-2">
                <v-card-text class="text-center py-8">
                    <v-icon size="40" color="grey lighten-1">mdi-map-marker-path</v-icon>
                    <div class="mt-3 mb-4 grey--text text--darken-1">
                        {{ $t('unit.list.enter_hint') }}
                    </div>
                    <v-btn color="primary" depressed class="normal-case"
                        @click="openUnitEditor">
                        {{ $t('unit.list.enter') }}
                    </v-btn>
                </v-card-text>
            </v-card>

            <v-card v-else outlined class="mt-2">
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn small color="grey darken-2"
                        class="normal-case" dark @click="preTestLink">
                        <v-icon small class="mr-2">mdi-pencil</v-icon>
                        {{ $t('quest.main.button_pretest_link') }}
                    </v-btn>
                    <v-btn small color="grey darken-2"
                        dark @click="postTestLink" class="mr-2 normal-case">
                        <v-icon small>mdi-pencil</v-icon>
                        {{ $t('quest.main.button_posttest_link') }}
                    </v-btn>
                    <v-btn small color="grey darken-2" dark
                        class="mr-2 normal-case" @click="addQuestion(null)">
                        <v-icon small class="mr-2">mdi-plus</v-icon>
                        {{ $t('quest.main.button_add_question') }}
                    </v-btn>
                </v-card-actions>
                <v-card class="ma-2" outlined v-for="(item, index) in questions" :key="index">
                    <v-card-subtitle class="black--text">
                        <v-avatar
                            color="#2E3338"
                            size="20" class="my-auto mr-2 body-2 white--text">{{ `${index + 1}` }}</v-avatar>
                        {{ $t('quest.main.title_label') + item.title }}
                        <v-icon @click="addQuestion(item.id)" class="ml-10">mdi-pencil</v-icon>
                        <v-icon @click="openDeleteQuestionDialog(item.id)" color="red"
                            class="ml-2">mdi-delete</v-icon>
                    </v-card-subtitle>
                    <v-card-subtitle v-for="(sample, id) in item.contents"
                        :key="id" class="text-body-1 black--text ml-5">
                        <v-chip disable :ripple="false" class="mr-2">
                            {{ $t(`quest.main.${sample.type}_frame`) }}
                        </v-chip>
                        {{ sample.title }}
                    </v-card-subtitle>
                </v-card>
            </v-card>
        </v-card>
        <StudentInvitationDialog v-model="inviteCodeDialog" :title="title" :isSample="true"></StudentInvitationDialog>
        <TemplateShareDialog v-if="canShare" v-model="shareDialog" :homework-id="homeworkId"
            :share-code.sync="shareCode"></TemplateShareDialog>
        <v-dialog v-model="deleteQuestionDialog" persistent width="600">
            <v-card class="pb-2">
                <v-card-title>
                    {{ $t('quest.main.deleteDialog.title') }}
                </v-card-title>
                <v-card-subtitle class="text-center text-h6 mt-2">
                    {{ $t('quest.main.deleteDialog.message') }}
                </v-card-subtitle>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="grey darken-2" class="normal-case"
                        text @click="deleteQuestionDialog = false">
                        {{ $t('button.cancel') }}
                    </v-btn>
                    <v-btn color="error" class="normal-case"
                        @click="deleteQuestion" :loading="loading">
                        {{ $t('button.confirm') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { diaryServices } from "@/services/diary.js";
import StudentInvitationDialog from '@/components/console/diary/component/StudentInvitationDialog.vue';
import TemplateShareDialog from '@/components/console/diary/TemplateShareDialog.vue';
import { templateUnitServices } from '@/services/templateUnit.js';

export default {
    name: 'Quest',
    components: { StudentInvitationDialog, TemplateShareDialog },
    data() {
        return {
            homeworkId: null,
            title: '',
            description: '',
            questions: [],
            /// diary_homework.template 有值 = 關卡式範本（spec §15）
            isUnitTemplate: false,
            /// GET shareCode 成功 = 擁有者的關卡式範本，可以分享（studio-api TemplateShare.spec.md）
            canShare: false,
            shareCode: null,
            shareDialog: false,
            inviteCode: '',
            inviteCodeDialog: false,
            deleteQuestionDialog: false,
            idToDelete: null,
            loading: false,
            lang: '',
        }
    },
    async created() {
        this.homeworkId = this.$route.params.id;
        const result = await diaryServices.getHomeworkDetail(this.homeworkId);
        if (result.success) {
            this.title = result.data.name;
            this.description = result.data.description;
            this.lang = result.data.speech_lang;
            /// template 有值 = 關卡式範本（spec §15）。
            /// 後端只在 ref_id 有值（老師領取後的日記本）時才刪掉這個欄位，
            /// 範本本身一定拿得到。
            this.isUnitTemplate = !!result.data.template;
        }
        if (this.isUnitTemplate) {
            await this.loadShareCode();
            /// 關卡式範本的題目由關卡編輯器管理，這一頁不列題目
            return;
        }
        const questionResult = await diaryServices.getSampleQuest(this.homeworkId);
        if (questionResult.success) {
            this.questions = questionResult.data.list;
        }
    },
    methods: {
        /// 404 = 不是擁有者或是領來的副本，不顯示分享按鈕。其他錯誤也先不顯示，不擋這一頁其他功能
        async loadShareCode() {
            try {
                const res = await templateUnitServices.getShareCode(this.homeworkId);
                this.shareCode = res?.data?.share_code || null;
                this.canShare = true;
            } catch (err) {
                this.canShare = false;
            }
        },
        openUnitEditor() {
            this.$router.push(`/diarySample/${this.homeworkId}/units`);
        },
        openDiarySetting() {
            // this.$router.push(`/diary/homework/${this.homeworkId}/edit`);
            this.$router.push(`/diarySample/${this.homeworkId}/edit`)
        },
        preTestLink() {
            console.log('preTestLink');
        },
        postTestLink() {
            console.log('postTestLink');
        },
        addQuestion(id) {
            if (id) {
                this.$router.push(`/diary/homework/${this.homeworkId}/quest/edit?questId=${id}&lang=${this.lang}`);
            } else {
                this.$router.push(`/diary/homework/${this.homeworkId}/quest/edit?lang=${this.lang}`);
            }
        },
        async checkInviteCode() {
            this.inviteCodeDialog = true;
        },
        openDeleteQuestionDialog(id) {
            this.idToDelete = id;
            this.deleteQuestionDialog = true;
        },
        async deleteQuestion() {
            this.loading = true;
            const id = this.idToDelete;
            const result = await diaryServices.deleteSampleQuest(this.homeworkId,
                id);

            if (result.data.return_code == '06000') {
                this.questions = this.questions.filter(item => item.id != id);
            }
            this.loading = false;
            this.deleteQuestionDialog = false;
        },
    },
    computed: {

    },
}
</script>
<style scoped>
.card-no-outline {
    border: none;
}
</style>